import { isDashboardV2Spec } from 'app/features/dashboard/api/utils';
import { convertSpecToWireFormat } from 'app/features/dashboard-scene/serialization/transformationCompat';

import {
  API_GROUP,
  API_VERSION,
  type DashboardTemplate,
  type DashboardTemplateSpec,
} from '../api/clients/dashboardtemplates/v1alpha1';

const KIND = 'DashboardTemplate';
const API_VERSION_FULL = `${API_GROUP}/${API_VERSION}`;

interface BuildBodyInput {
  // Provide `name` for replace (PUT) or `generateName` for create (POST).
  name?: string;
  generateName?: string;
  // Optional optimistic-concurrency token on replace.
  resourceVersion?: string;
  // Annotations are dropped from the wire body when empty.
  annotations?: Record<string, string>;
  spec: DashboardTemplateSpec;
}

// buildDashboardTemplateBody centralizes the apiVersion + kind + metadata
// envelope used by create/replace/restore wire calls.
// Normalizes the embedded v2 dashboard spec to the wire format the currently
// resolved v2 API expects (v2 vs v2beta1 transformation shape) so callers
// can pass an in-memory or historical spec without worrying about wire shape.
export function buildDashboardTemplateBody(input: BuildBodyInput): DashboardTemplate {
  const annotations = input.annotations && Object.keys(input.annotations).length > 0 ? input.annotations : undefined;

  const dashboard = isDashboardV2Spec(input.spec.dashboard)
    ? convertSpecToWireFormat(input.spec.dashboard)
    : input.spec.dashboard;

  return {
    apiVersion: API_VERSION_FULL,
    kind: KIND,
    metadata: {
      ...(input.name ? { name: input.name } : {}),
      ...(input.generateName ? { generateName: input.generateName } : {}),
      ...(input.resourceVersion ? { resourceVersion: input.resourceVersion } : {}),
      ...(annotations ? { annotations } : {}),
    },
    spec: { ...input.spec, dashboard },
  };
}

// preserveOuterSpec returns a fresh spec with the catalog fields copied from
// the current template and only the embedded dashboard payload swapped in.
// Used by save (FE editor → template) and restore (history → template).
export function preserveOuterSpec(
  outerSpec: DashboardTemplateSpec,
  dashboard: DashboardTemplateSpec['dashboard']
): DashboardTemplateSpec {
  return {
    title: outerSpec.title,
    description: outerSpec.description,
    tags: outerSpec.tags,
    dashboardVersion: outerSpec.dashboardVersion,
    dashboard,
  };
}
